import React from 'react';
import { brandKitAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { ExternalLink, Download, FileText, Image as ImageIcon } from 'lucide-react';

const isImg = (f) => f && ((f.content_type || '').startsWith('image/') || /\.(png|jpe?g|gif|webp|svg)$/i.test(f.name || ''));

const FileChip = ({ file }) => file ? (
  <a href={brandKitAPI.fileUrl(file.id)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs border rounded-md px-2 py-1 hover:bg-muted" data-testid={`collab-file-${file.id}`}>
    <Download className="w-3 h-3" /> {file.name?.length > 28 ? file.name.slice(0, 26) + '…' : file.name}
  </a>
) : null;

const Thumb = ({ file }) => isImg(file)
  ? <img src={brandKitAPI.fileUrl(file.id)} alt={file.name} className="h-24 w-full rounded-md border object-contain bg-white" />
  : <div className="h-24 w-full rounded-md border bg-muted/40 flex items-center justify-center"><FileText className="w-6 h-6 text-muted-foreground" /></div>;

const Section = ({ title, children, testId }) => (
  <Card data-testid={testId}><CardHeader className="pb-2"><CardTitle className="text-base">{title}</CardTitle></CardHeader><CardContent>{children}</CardContent></Card>
);

export const CollabBrandKit = ({ kit }) => {
  const d = kit?.data || {};
  const files = kit?.briefing_files || [];
  const empty = !d.logos?.length && !d.colores?.length && !d.tipografias?.length && !d.ejemplos?.length && !d.manual && !d.drive_link && !files.length;
  if (empty) return <p className="text-sm text-muted-foreground text-center py-10" data-testid="collab-brandkit-empty">Este cliente todavía no tiene material de marca cargado.</p>;
  return (
    <div className="grid md:grid-cols-2 gap-4" data-testid="collab-brandkit">
      {d.drive_link && (
        <a href={d.drive_link} target="_blank" rel="noreferrer" className="md:col-span-2 flex items-center gap-2 p-3 rounded-lg border bg-blue-50 text-blue-800 text-sm hover:bg-blue-100" data-testid="collab-drive-link">
          <ExternalLink className="w-4 h-4" /> Carpeta de Drive con todo el material del cliente
        </a>
      )}
      {d.colores?.length > 0 && (
        <Section title="Colores" testId="collab-colores">
          <div className="flex flex-wrap gap-3">
            {d.colores.map((c) => (
              <div key={c.id} className="flex items-center gap-2">
                <span className="w-9 h-9 rounded-lg border shadow-sm" style={{ background: c.hex }} />
                <div><p className="text-sm font-medium">{c.nombre || 'Color'}</p><p className="text-xs text-muted-foreground font-mono">{c.hex}</p></div>
              </div>
            ))}
          </div>
        </Section>
      )}
      {d.tipografias?.length > 0 && (
        <Section title="Tipografías" testId="collab-tipografias">
          <div className="space-y-2">
            {d.tipografias.map((t) => (
              <div key={t.id} className="flex items-center justify-between gap-2 text-sm">
                <div><p className="font-medium">{t.nombre || 'Tipografía'}</p>{t.uso && <p className="text-xs text-muted-foreground">{t.uso}</p>}</div>
                <div className="flex gap-1">{t.url && <a href={t.url} target="_blank" rel="noreferrer" className="text-xs text-blue-600 inline-flex items-center gap-1"><ExternalLink className="w-3 h-3" /> enlace</a>}<FileChip file={t.file} /></div>
              </div>
            ))}
          </div>
        </Section>
      )}
      {d.logos?.length > 0 && (
        <Section title="Logos" testId="collab-logos">
          <div className="space-y-3">
            {d.logos.map((l) => (
              <div key={l.id}>
                <p className="text-sm font-medium mb-1">{l.nombre}</p>
                <div className="grid grid-cols-3 gap-2">{(l.files || []).map((f) => <div key={f.id} className="space-y-1"><Thumb file={f} /><FileChip file={f} /></div>)}</div>
              </div>
            ))}
          </div>
        </Section>
      )}
      {d.ejemplos?.length > 0 && (
        <Section title="Ejemplos de estilo" testId="collab-ejemplos">
          <div className="grid grid-cols-3 gap-2">
            {d.ejemplos.map((e) => <div key={e.id} className="space-y-1"><Thumb file={e.file} />{e.nota && <p className="text-[11px] text-muted-foreground">{e.nota}</p>}</div>)}
          </div>
        </Section>
      )}
      {(d.manual || d.canva_links?.length > 0 || files.length > 0) && (
        <Section title="Manual y referencias" testId="collab-manual">
          <div className="flex flex-wrap gap-2">
            {d.manual && <FileChip file={d.manual} />}
            {(d.canva_links || []).map((c) => c.url && <a key={c.id} href={c.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs border rounded-md px-2 py-1 hover:bg-muted"><ExternalLink className="w-3 h-3" /> {c.nombre || 'Canva'}</a>)}
            {files.map((f) => <FileChip key={f.id} file={f} />)}
          </div>
        </Section>
      )}
      {d.usos_incorrectos?.texto && (
        <Section title="Usos incorrectos (evitar)" testId="collab-usos-incorrectos">
          <p className="text-sm whitespace-pre-wrap">{d.usos_incorrectos.texto}</p>
          {d.usos_incorrectos.imagenes?.length > 0 && <div className="grid grid-cols-4 gap-2 mt-2">{d.usos_incorrectos.imagenes.map((f) => <Thumb key={f.id} file={f} />)}</div>}
        </Section>
      )}
    </div>
  );
};
